import { useTranslation } from 'react-i18next';
import { Wrench, type LucideIcon } from 'lucide-react';
import { SectionWrapper, SectionHeader } from '@/components/common';

interface ServiceItem {
  title: string;
  description: string;
  icon?: LucideIcon;
}

interface ServiceGridProps {
  title?: string;
  subtitle?: string;
  services?: ServiceItem[];
  serviceType?: string;
  bgColor?: string;
  iconBgColor?: string;
  columns?: 2 | 3 | 4;
  className?: string;
}

export function ServiceGrid({
  title,
  subtitle,
  services,
  serviceType,
  bgColor = 'bg-blue-50',
  iconBgColor = 'bg-blue-600',
  columns = 3,
  className,
}: ServiceGridProps) {
  const { t } = useTranslation('services');

  // If services not provided, build from translations
  const displayServices: ServiceItem[] = services || (serviceType ? [1, 2, 3, 4, 5, 6].map((num) => ({
    title: t(`${serviceType}.servicesOffered.service${num}.title`),
    description: t(`${serviceType}.servicesOffered.service${num}.description`),
  })) : []);

  const displayTitle = title || (serviceType ? t(`${serviceType}.servicesOffered.title`) : '');
  const displaySubtitle = subtitle || (serviceType ? t(`${serviceType}.servicesOffered.subtitle`) : '');

  const gridCols = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-2 lg:grid-cols-3',
    4: 'md:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <SectionWrapper bgColor="bg-white" className={className}>
      <SectionHeader
        title={displayTitle}
        subtitle={displaySubtitle}
        subtitleClassName="text-lg text-gray-600 max-w-2xl mx-auto"
        className="mb-12"
      />

      <div className={`grid ${gridCols[columns]} gap-6`}>
        {displayServices.map((service, index) => {
          const Icon = service.icon ?? Wrench;
          return (
            <div
              key={index}
              className={`${bgColor} rounded-xl p-6 hover:shadow-lg transition-shadow`}
            >
              <div className={`w-12 h-12 ${iconBgColor} rounded-xl flex items-center justify-center mb-4`}>
                <Icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {service.title}
              </h3>
              <p className="text-gray-600 text-sm">
                {service.description}
              </p>
            </div>
          );
        })}
      </div>
    </SectionWrapper>
  );
}
